'use client';

import { useState, useTransition } from 'react';

import {
  finishEmissorLogin,
  requestEmissorOtp,
  verifyEmissorOtp,
  type EmissorAuthResult,
} from './actions';

import { createSupabaseBrowserClient } from '@/lib/supabase/browser';

type Stage = 'email' | 'otp' | 'mfa';

export function EmissorLoginForm({ initialError }: { initialError?: string | null }) {
  const [stage, setStage] = useState<Stage>('email');
  const [email, setEmail] = useState('');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(initialError ?? null);
  const [pending, startTransition] = useTransition();
  const [verifying, setVerifying] = useState(false);

  function submitEmail(formData: FormData) {
    setError(null);
    startTransition(async () => {
      const result: EmissorAuthResult = await requestEmissorOtp(formData);
      if (!result.ok) {
        setError(result.error ?? 'Não foi possível continuar.');
        return;
      }
      setStage('otp');
    });
  }

  function submitOtp(formData: FormData) {
    setError(null);
    startTransition(async () => {
      const result: EmissorAuthResult = await verifyEmissorOtp(formData);
      if (!result.ok) {
        setError(result.error ?? 'Código incorreto.');
        return;
      }
      if (!result.mfaFactorId) {
        setError('Segundo fator indisponível. Refaça o login.');
        return;
      }
      setFactorId(result.mfaFactorId);
      setStage('mfa');
    });
  }

  /**
   * Roda no browser porque mfa.challenge/verify exigem a sessão AAL1 que
   * acabou de ser criada nos cookies. Mesmo padrão de (auth)/entrar.
   */
  async function submitMfa(formData: FormData): Promise<void> {
    const code = String(formData.get('code') ?? '').replace(/\s/g, '');
    if (!/^\d{6}$/.test(code)) {
      setError('Código precisa ter 6 dígitos.');
      return;
    }
    if (!factorId) {
      setError('Sessão do segundo fator perdida. Refaça o login.');
      setStage('email');
      return;
    }

    setError(null);
    setVerifying(true);
    try {
      const supabase = createSupabaseBrowserClient();

      const challengeRes = await supabase.auth.mfa.challenge({ factorId });
      if (challengeRes.error || !challengeRes.data) {
        setError(challengeRes.error?.message ?? 'Falha ao iniciar a verificação.');
        return;
      }

      const verifyRes = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeRes.data.id,
        code,
      });
      if (verifyRes.error) {
        setError('Código inválido ou expirado. Confira o app autenticador.');
        return;
      }

      // Sessão já é AAL2 nos cookies. O servidor confere antes de liberar.
      const finish = await finishEmissorLogin();
      if (!finish.ok) {
        setError(finish.error ?? 'Não foi possível concluir o login.');
        return;
      }
      window.location.assign(finish.redirectTo ?? '/emissor');
    } catch (err) {
      console.error('[emissor submitMfa]', err);
      setError('Erro inesperado. Tente de novo.');
    } finally {
      setVerifying(false);
    }
  }

  const busy = pending || verifying;

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
      <h1 className="text-2xl font-semibold">Emissor de NFS-e</h1>
      <p className="text-ink-muted mt-2 text-sm">Clareou Soluções Digitais</p>

      {stage === 'email' ? (
        <form action={submitEmail} className="mt-8 flex flex-col gap-4">
          <label className="flex flex-col gap-2 text-sm">
            E-mail
            <input
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border-border min-h-[44px] rounded-lg border px-3"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="bg-trakd-fill text-on-brand min-h-[44px] rounded-lg font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? 'Enviando…' : 'Continuar'}
          </button>
        </form>
      ) : null}

      {stage === 'otp' ? (
        <form action={submitOtp} className="mt-8 flex flex-col gap-4">
          <input type="hidden" name="email" value={email} />
          <label className="flex flex-col gap-2 text-sm">
            Código enviado para {email}
            <input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              autoFocus
              className="border-border min-h-[44px] rounded-lg border px-3 tracking-[0.5em]"
            />
          </label>
          <p className="text-ink-subtle text-xs">
            Digite o código aqui. Não clique no link do e-mail, ele encerra esse login e você vai
            precisar de um código novo.
          </p>
          <button
            type="submit"
            disabled={busy}
            className="bg-trakd-fill text-on-brand min-h-[44px] rounded-lg font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? 'Verificando…' : 'Verificar'}
          </button>
          <button
            type="button"
            onClick={() => setStage('email')}
            className="text-ink-muted hover:text-ink min-h-[44px] text-sm underline transition-colors"
          >
            Usar outro e-mail
          </button>
        </form>
      ) : null}

      {stage === 'mfa' ? (
        <form action={submitMfa} className="mt-8 flex flex-col gap-4">
          <label className="flex flex-col gap-2 text-sm">
            Código do app autenticador
            <input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              autoFocus
              className="border-border min-h-[44px] rounded-lg border px-3 tracking-[0.5em]"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="bg-trakd-fill text-on-brand min-h-[44px] rounded-lg font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? 'Verificando…' : 'Entrar'}
          </button>
          <button
            type="button"
            onClick={() => {
              setStage('email');
              setFactorId(null);
              setError(null);
            }}
            className="text-ink-muted hover:text-ink min-h-[44px] text-sm underline transition-colors"
          >
            Usar outro e-mail
          </button>
        </form>
      ) : null}

      {error ? <p className="text-error mt-4 text-sm">{error}</p> : null}
    </div>
  );
}
